import {Empty, toEither, Async, isLoaded} from 'Core/models/Async';
import {
  getEnrollmentFileApplicationDetail,
  changeEnrollmentApplicationDetailFilters,
  getEnrollmentFileByCaseRequestId,
  changeEnrollmentFile,
} from 'Enrollment/ducks/enrollmentFileProcessing';
import React, {useEffect} from 'react';
import * as qs from 'query-string';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useHistory} from 'react-router';
import {ReducerState} from 'rootReducer';
import {renderAsync} from 'Shared/components/RenderAsync';
import {EnrollmentRouteConstants} from '../../Routes';
import './EnrollmentFileDetails.sass';
import './EnrollmentFileRejectDetails.sass';
import {Link} from 'react-router-dom';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {pipe} from 'fp-ts/lib/pipeable';
import {EnrollmentApplicationStatus} from 'Shared/constants/EnrollmentApplicationStatus';
import * as E from 'fp-ts/lib/Either';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {withStringFilterProperties, withSelectFilterProperties} from 'Shared/components/PagedView/Filter';
import {ESREnrollmentFile} from 'Enrollment/models/EnrollmentFile';
import CardField from 'Shared/components/CardField';
import './EnrollmentFileDetails.sass';
import Loading from 'Shared/components/Loading';
import {FilterEnum, enrollmentApplicationDetailFiltersEmpty} from 'Enrollment/ducks/enrollmentFileProcessing';
import {FilterOptionValue} from 'Core/models/FilterOption';
import {FinalDispositionStatus} from 'Shared/constants/FinalDispositionStatus';
import {toFormattedDate} from 'Shared/utilities/DateFormatter';
import './EnrollmentFileApplicationDetails.sass';
import ModularHost from 'Shared/components/ModularHost/ModularHost';
import ToolbarHeader, {ExportToolbarHeaderIconWithCustomError} from 'Shared/components/Toolbar/ToolbarHeader';
import {FileExtension} from 'Shared/components/FileDownloadButton';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';

type EnrollmentFileProps = {
  enrollmentFile: Async<ESREnrollmentFile>;
};
const requestTypeOptions: FilterEnum[] = [
  {name: 'Coverage Election', value: 'Coverage Election'},
  {name: 'Non-Coverage Demographic Change', value: 'Non-Coverage Demographic Change'},
  {name: 'No Changes Detected', value: 'No Changes Detected'},
  {name: 'Admin Change', value: 'Admin Change'},
  {name: 'Coverage Change', value: 'Coverage Change'},
  {name: 'Coverage Termination', value: 'Coverage Termination'},
];

const finalDispositionStatusOptions: FilterEnum[] = [
  {name: 'Pending', value: FinalDispositionStatus.Pending.toString()},
  {name: 'Completed', value: FinalDispositionStatus.Complete.toString()},
];
const EnrollmentFileApplicationDetails = () => {
  const dispatch = useDispatch();
  const {search} = useLocation();
  const history = useHistory();
  const {caseRequestId, finalDispositionStatus} = qs.parse(search) as {
    caseRequestId: string | undefined;
    finalDispositionStatus: string | undefined;
  };
  const statusId = (qs.parse(search).statusId as unknown) as EnrollmentApplicationStatus;

  const {enrollmentFileApplication, enrollmentApplicationDetailFilters, enrollmentFile} = useSelector(
    (state: ReducerState) => state.enrollmentFileProcessing
  );
  const changeFilter = withValueChangedHook(
    enrollmentApplicationDetailFilters,
    changeEnrollmentApplicationDetailFilters,
    dispatch
  );

  const filterProperties = {
    changeFilter,
    filterData: enrollmentApplicationDetailFilters,
  };

  useEffect(() => {
    dispatch(
      changeEnrollmentApplicationDetailFilters({
        ...enrollmentApplicationDetailFilters,
        finalDispositionStatus:
          finalDispositionStatusOptions
            .filter(x => x.value === finalDispositionStatus)
            .map(FilterOptionValue.SelectedOption)[0] || FilterOptionValue.EmptyOption(),
        id: caseRequestId as string,
      })
    );
  }, [dispatch, caseRequestId, finalDispositionStatus]);

  useEffect(() => {
    if (caseRequestId) {
      dispatch(getEnrollmentFileByCaseRequestId(caseRequestId!));
    } else {
      history.push(EnrollmentRouteConstants.ENROLLMENT_FILE_PROCESSING);
    }
    return () => {
      dispatch(changeEnrollmentFile(new Empty()));
    };
  }, [dispatch, history, caseRequestId]);

  useEffect(() => {
    dispatch(
      getEnrollmentFileApplicationDetail(undefined, {
        ...enrollmentApplicationDetailFilters,
        id: caseRequestId as string,
        statusId,
      })
    );
  }, [dispatch, caseRequestId, statusId, enrollmentApplicationDetailFilters]);

  useEffect(() => {
    history.listen(location => {
      if (!location.pathname.includes('/applications')) {
        dispatch(changeEnrollmentApplicationDetailFilters({...enrollmentApplicationDetailFiltersEmpty}));
      }
    });
  }, [dispatch, enrollmentApplicationDetailFilters, history]);

  const RenderHeader = ({enrollmentFile}: EnrollmentFileProps) => {
    return pipe(
      toEither(enrollmentFile),
      E.fold(
        error => (
          <div>
            {renderAsync(error, () => (
              <Loading />
            ))}
          </div>
        ),
        enrollmentFile => {
          return (
            <div className="enrollment-file-details-header">
              <div className="enrollment-file-details-header__icon-container">
                <i className="icon-container__file-icon"></i>
              </div>
              <div className="enrollment-file-details__file-details">
                <CardField
                  className="enrollment-file-details__details-field"
                  name="Agency Name"
                  value={enrollmentFile?.agencyName}
                />
                <CardField
                  className="enrollment-file-details__details-field"
                  name="Load Date"
                  value={toFormattedDate(enrollmentFile?.createdDate?.toString())}
                />
                <CardField
                  className="enrollment-file-details__details-field"
                  name="Account Number"
                  value={enrollmentFile?.accountNumber}
                />
                <CardField
                  className="enrollment-file-details__details-field"
                  name="File Name"
                  value={enrollmentFile?.fileName}
                />
              </div>
            </div>
          );
        }
      )
    );
  };

  return (
    <>
      <RenderHeader enrollmentFile={enrollmentFile} />
      <ModularHost>
        <ToolbarHeader title= "Enrollment File Application Details">
          <ExportToolbarHeaderIconWithCustomError
            title="Export Enrollment File Applications"
            downloadedFileName="Enrollment_File_Application_Details"
            extension={FileExtension.CSV}
            relativeFileLocation={`/enrollmentFiles/applicationstatus/esr/csv${addFilterParameters({
                ...enrollmentApplicationDetailFilters,
              })}`}
            totalRecordCount={isLoaded(enrollmentFileApplication) ? enrollmentFileApplication.Data.totalCount : 0}
          />
        </ToolbarHeader>
        <PagedView
          pagedViewKey="enrollmentFileApplicationDetails"
          data={enrollmentFileApplication}
          loadMore={page => dispatch(getEnrollmentFileApplicationDetail(page, enrollmentApplicationDetailFilters))}
        >
          {columnProps => (
            <>
              <Column
                {...columnProps}
                title="Request Type"
                filterProperties={withSelectFilterProperties(filterProperties, 'requestType', requestTypeOptions)}
                default
              >
                {esrHistoryResult => esrHistoryResult.requestType}
              </Column>
              <Column
                {...columnProps}
                title="Account Number"
                filterProperties={withStringFilterProperties(filterProperties, 'accountNumber')}
                default
              >
                {esrHistoryResult => esrHistoryResult.accountNumber}
              </Column>
              <Column
                {...columnProps}
                title="Product"
                filterProperties={withStringFilterProperties(filterProperties, 'product')}
                default
              >
                {esrHistoryResult => esrHistoryResult.product}
              </Column>
              <Column
                {...columnProps}
                title="SSN"
                filterProperties={withStringFilterProperties(filterProperties, 'maskedIdentifier')}
                default
              >
                {esrHistoryResult => esrHistoryResult.maskedIdentifier}
              </Column>
              <Column
                {...columnProps}
                title="First Name"
                filterProperties={withStringFilterProperties(filterProperties, 'firstName')}
                default
              >
                {esrHistoryResult => esrHistoryResult.firstName}
              </Column>
              <Column
                {...columnProps}
                title="Last Name"
                filterProperties={withStringFilterProperties(filterProperties, 'lastName')}
                default
              >
                {esrHistoryResult => esrHistoryResult.lastName}
              </Column>
              <Column {...columnProps} title="BP Status" default>
                {esrHistoryResult => (
                  <Link
                    id={`enrollment-app-bp-status-link`}
                    className="enrollment-app-link"
                    to={EnrollmentRouteConstants.ENROLLMENT_FILE_APPLICATION_BP_STATUS_DETAILS(
                      esrHistoryResult.requestId!,
                      caseRequestId!.toString(),
                      parseInt(finalDispositionStatus!, 10) as FinalDispositionStatus,
                      statusId
                    )}
                  >
                    {esrHistoryResult.bpStatus}
                  </Link>
                )}
              </Column>
              <Column {...columnProps} title="AWD Status" default>
                {esrHistoryResult => (
                  <Link
                    className="enrollment-app-link"
                    to={EnrollmentRouteConstants.ENROLLMENT_FILE_APPLICATION_AWD_STATUS_DETAILS(
                      esrHistoryResult.requestId!,
                      caseRequestId!.toString(),
                      esrHistoryResult.awdSystemID!.toString(),
                      parseInt(finalDispositionStatus!, 10) as FinalDispositionStatus,
                      statusId
                    )}
                  >
                    {esrHistoryResult.awdStatus}
                  </Link>
                )}
              </Column>
              <Column {...columnProps} title="L70 Status" default>
                {esrHistoryResult => esrHistoryResult.l70Status}
              </Column>
              <Column
                {...columnProps}
                title="Final Disposition"
                filterProperties={withSelectFilterProperties(
                  filterProperties,
                  'finalDispositionStatus',
                  finalDispositionStatusOptions
                )}
                default
              >
                {esrHistoryResult => esrHistoryResult.finalDisposition}
              </Column>
            </>
          )}
        </PagedView>
      </ModularHost>
    </>
  );
};

export default EnrollmentFileApplicationDetails;
